import { debounce, zxcvbnAsync, zxcvbnOptions, ZxcvbnResult } from '@zxcvbn-ts/core';
import translationKeys from '@zxcvbn-ts/core/src/data/translationKeys.ts';
import { OptionsType } from '@zxcvbn-ts/core/src/types.ts';
import * as zxcvbnCommonPackage from '@zxcvbn-ts/language-common';
import * as zxcvbnDePackage from '@zxcvbn-ts/language-de';
import * as zxcvbnEnPackage from '@zxcvbn-ts/language-en';
import { matcherPwnedFactory } from '@zxcvbn-ts/matcher-pwned';
import { MaybeRef, readonly, Ref, ref, unref, watch } from 'vue';

/**
 * Plugin options
 * - Always use the [Levenshtein distance](https://en.wikipedia.org/wiki/Levenshtein_distance) since devices today are powerful enough to calculate it fast.
 */
type ZxcvbnOptions = OptionsType & {
  usePwned?: boolean;
};

const defaultOptions: ZxcvbnOptions = {
  usePwned: true,
  dictionary: {
    userInputs: ['ptw', 'dosimetry', 'radiology', 'oncology', 'radiation'],
    // Common and English dictionaries are always included
    ...zxcvbnCommonPackage.dictionary,
    ...zxcvbnEnPackage.dictionary,
    ...zxcvbnDePackage.dictionary,
  },
  graphs: {
    ...zxcvbnCommonPackage.adjacencyGraphs,
  },
  // always used to improve matching quality
  useLevenshteinDistance: true,
  translations: translationKeys,
};

const emptyResult: ZxcvbnResult = {
  calcTime: 0,
  password: '',
  guesses: 1,
  guessesLog10: 0,
  sequence: [],
  crackTimesSeconds: {
    onlineThrottling100PerHour: 0,
    onlineNoThrottling10PerSecond: 0,
    offlineSlowHashing1e4PerSecond: 0,
    offlineFastHashing1e10PerSecond: 0,
  },
  crackTimesDisplay: {
    onlineThrottling100PerHour: 'seconds',
    onlineNoThrottling10PerSecond: 'ltSecond',
    offlineSlowHashing1e4PerSecond: 'ltSecond',
    offlineFastHashing1e10PerSecond: 'ltSecond',
  },
  score: 0,
  feedback: { warning: null, suggestions: [] },
};

export function createZxcvbn(options: ZxcvbnOptions = defaultOptions) {
  const install = () => {
    if (options.usePwned) {
      const matcherPwned = matcherPwnedFactory(fetch, zxcvbnOptions);
      zxcvbnOptions.addMatcher('pwned', matcherPwned);
    }

    zxcvbnOptions.setOptions(options);
  };
  return { install, options };
}

/**
 * Get custom user inputs from either an Array or Object.
 * @param password
 * @param userInputs
 */
export function useZxcvbn(
  password: Ref<string | undefined>,
  userInputs: MaybeRef<string[]> = ref([]),
) {
  const result = ref<ZxcvbnResult>(emptyResult);

  watch([password, userInputs], async () => {
    debounce(async () => {
      result.value = await zxcvbnAsync(password.value ?? '', unref(userInputs));
    }, 200)();
  });

  return readonly(result);
}
